import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";

import { useToast } from "../../../components/Toast";
import { Button, Cargando, ErrorCarga } from "../../../components/ui";
import {
  actualizarCicloConfig,
  fetchCicloConfig,
  type CicloConfig,
} from "../api";

const CAMPOS: {
  key: keyof CicloConfig;
  titulo: string;
  descripcion: string;
  tono: string;
  max?: number;
}[] = [
  {
    key: "dia_lecturas_inicio",
    titulo: "Toma de lecturas",
    descripcion: "Inicio del ciclo: registrar consumos del mes.",
    tono: "bg-brand-50 ring-brand-200",
  },
  {
    key: "dia_cierre_inicio",
    titulo: "Cierre del periodo",
    descripcion: "Validar lecturas, prevalidar, calcular subsidios.",
    tono: "bg-neutral-50 ring-neutral-200",
  },
  {
    key: "dia_emision_inicio",
    titulo: "Emisión de boletas",
    descripcion: "Generar boletas y proforma del cierre.",
    tono: "bg-emerald-50 ring-emerald-200",
  },
  {
    key: "dia_cobranza_inicio",
    titulo: "Cobranza",
    descripcion: "Registrar pagos y dar seguimiento a saldos.",
    tono: "bg-amber-50 ring-amber-200",
  },
  {
    key: "dia_morosidad_inicio",
    titulo: "Morosidad y cortes",
    descripcion: "Órdenes de corte y cierre administrativo.",
    tono: "bg-rose-50 ring-rose-200",
  },
];

export function CicloTab(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const cfgQ = useQuery({
    queryKey: ["empresa", "ciclo-config"],
    queryFn: fetchCicloConfig,
  });

  const [form, setForm] = useState<CicloConfig | null>(null);

  useEffect(() => {
    if (cfgQ.data && form === null) setForm(cfgQ.data);
  }, [cfgQ.data, form]);

  const mut = useMutation({
    mutationFn: (payload: CicloConfig) => actualizarCicloConfig(payload),
    onSuccess: (data) => {
      qc.setQueryData(["empresa", "ciclo-config"], data);
      toast.success("Calendario del ciclo actualizado");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar");
    },
  });

  function setCampo(key: keyof CicloConfig, valor: number): void {
    setForm((prev) => (prev ? { ...prev, [key]: valor } : prev));
  }

  function fasesValidas(cfg: CicloConfig): string | null {
    const dias = [
      cfg.dia_lecturas_inicio,
      cfg.dia_cierre_inicio,
      cfg.dia_emision_inicio,
      cfg.dia_cobranza_inicio,
      cfg.dia_morosidad_inicio,
    ];
    if (new Set(dias).size !== dias.length) {
      return "Cada fase debe iniciar en un día distinto del mes.";
    }
    // El ciclo puede cruzar el fin de mes: se exige que los 5 días formen
    // una rotación del orden (lecturas→…→morosidad), es decir, exactamente
    // un "salto hacia atrás" recorriendo el círculo. (Mismo criterio que el
    // backend en services/empresa/ciclo_config.py.)
    let descensos = 0;
    for (let i = 0; i < dias.length; i++) {
      const a = dias[i]!;
      const b = dias[(i + 1) % dias.length]!;
      if (b < a) descensos++;
    }
    if (descensos !== 1) {
      return "Las fases deben sucederse en orden (lecturas → cierre → emisión → cobranza → morosidad). El ciclo puede cruzar el fin de mes (ej. emisión el 30 y cobranza el 1), pero no puede desordenarse.";
    }
    return null;
  }

  if (cfgQ.isPending) {
    return <Cargando texto="Cargando configuración…" />;
  }
  if (cfgQ.isError || !form) {
    return (
      <ErrorCarga
        error={cfgQ.error}
        mensaje="No se pudo cargar la configuración"
        onReintentar={() => void cfgQ.refetch()}
      />
    );
  }

  const error = fasesValidas(form);

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-neutral-600">
        Día del mes en que arranca cada fase. La fase termina donde empieza la
        siguiente. El ciclo puede cruzar el fin de mes (ej. lectura el 24,
        emisión el 30, cobranza el 1 y morosidad el 10 del mes siguiente). El
        dashboard usa estos valores para priorizar atajos según el día actual.
      </p>

      <div className="space-y-3">
        {CAMPOS.map((c) => (
          <div
            key={c.key}
            className={`rounded-lg p-4 ring-1 ${c.tono} flex items-start gap-4`}
          >
            <div className="flex-1">
              <div className="text-sm font-semibold text-neutral-900">
                {c.titulo}
              </div>
              <div className="text-xs text-neutral-600 mt-0.5">
                {c.descripcion}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-600">Inicia día</label>
              <input
                type="number"
                min={1}
                max={c.max ?? 31}
                value={form[c.key]}
                onChange={(e) => setCampo(c.key, Number(e.target.value))}
                className="w-16 rounded-md border border-neutral-450 bg-white px-2 py-1.5 text-center text-sm shadow-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="text-sm text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-neutral-200 pt-4">
        <Button
          variant="secondary"
          onClick={() => setForm(cfgQ.data ?? null)}
          disabled={mut.isPending}
        >
          Restablecer
        </Button>
        <Button
          icon={Save}
          onClick={() => mut.mutate(form)}
          disabled={mut.isPending || error !== null}
        >
          {mut.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}
